import { Style, Fill, Stroke, Text } from 'ol/style';
import defaultTheme from './styles/defaultTheme';
import { mergeDeep } from './utils/objectUtils';
export const getPropFromFeature = prop => feature => feature.getProperties()[prop];
/**
 * Returns feature prop selector
 * @param {string} prop Prop key
 * @returns {function(object):number}
 */

export const getNumberFromFeatureProps = (prop = 'Value') => feature => +getPropFromFeature(prop)(feature);
export const getLineValueFromFeature = feature => +getPropFromFeature('IsolineValue')(feature);
export const getAreaValueFromFeature = feature => {
  const from = getPropFromFeature('From')(feature);
  const to = getPropFromFeature('To')(feature);
  return Math.min(from, to);
};
export const getAreaLabelFromFeature = feature => getPropFromFeature('Value')(feature);
/**
 *
 * @param {object} feature Feature
 * @param {number} resolution Resolution
 * @param {object} params Params
 * @param {object} viewOptions View options
 */
// eslint-disable-next-line no-unused-vars

function defaultLabelSelector(feature, resolution, params, viewOptions) {
  return getPropFromFeature('Value')(feature);
}

class StyleFactory {
  static createText(text, textTheme) {
    if (text === null || text === undefined) {
      return null;
    }

    if (!this.textStyle) {
      this.textStyle = new Text();
    }

    this.textStyle.setOffsetX(textTheme.offsetX);
    this.textStyle.setOffsetY(textTheme.offsetY);
    this.textStyle.setFont(textTheme.font);
    this.textStyle.setText(text.toString());
    this.textStyle.setFill(new Fill(textTheme.fill));
    this.textStyle.setStroke(new Stroke(textTheme.stroke));
    this.textStyle.setTextAlign(textTheme.textAlign);
    return this.textStyle;
  }

  static createFill(theme) {
    if (!this.fill) {
      this.fill = new Fill();
    }

    this.fill.setColor(theme.color);
    return theme && this.fill;
  }

  static createStroke(theme) {
    if (!this.stroke) {
      this.stroke = new Stroke();
    }

    this.stroke.setColor(theme.color);
    this.stroke.setWidth(theme.width);
    return theme && this.stroke;
  }

  static createStyle({
    valueSelector = null,
    labelSelector = defaultLabelSelector,
    themeSelector = null,
    geometry = null,
    image = null
  }) {
    return (viewOptions, view, theme = {}, additionalOptions) => (feature, resolution, params) => {
      const value = valueSelector ? valueSelector(feature, resolution, params) : feature;
      const label = labelSelector ? labelSelector(feature, resolution, params, viewOptions) : null;
      const featureTheme = (typeof themeSelector === 'function' ? themeSelector(value, theme, feature, resolution, params, viewOptions, view, additionalOptions) : themeSelector) || {};
      const styleTheme = {
        text: mergeDeep({}, theme.text, featureTheme.text),
        stroke: view === 'area' ? {
          color: 'transparent'
        } : Object.assign({}, theme.stroke, featureTheme, featureTheme.stroke),
        fill: Object.assign({}, theme.fill, featureTheme, featureTheme.fill),
        zIndex: theme.zIndex || featureTheme.zIndex || 0
      };
      const style = new Style({
        text: StyleFactory.createText(label, styleTheme.text),
        image: typeof image === 'function' ? image(feature, resolution, params, viewOptions, theme) : image,
        geometry: typeof geometry === 'function' ? geometry(feature, resolution, params, viewOptions, additionalOptions) : geometry,
        fill: StyleFactory.createFill(styleTheme.fill),
        stroke: StyleFactory.createStroke(styleTheme.stroke),
        zIndex: styleTheme.zIndex
      });
      return style;
    };
  }

  static createStyleWithLabel(styleOptions) {
    const defaultStyle = StyleFactory.createStyle({ ...styleOptions,
      labelSelector: null
    });

    const labelStyleByCondition = (...args) => (feature, resolution, params) => {
      const type = feature.getGeometry().getType();

      if (type === 'Point') {
        const labelStyle = StyleFactory.createStyle({
          labelSelector: styleOptions.labelSelector
        });
        return labelStyle(...args)(feature, resolution, params);
      }

      return null;
    };

    return [defaultStyle, labelStyleByCondition];
  }

  static createBorderStyle(borderTheme) {
    const theme = mergeDeep({}, defaultTheme.border, borderTheme);

    if (!this.border) {
      this.border = new Style({
        stroke: new Stroke()
      });
    }

    const borderStroke = this.border.getStroke();
    borderStroke.setColor(theme.color);
    borderStroke.setWidth(theme.width);
    return this.border;
  }

  static createDefaultGridStyle(themeSelector) {
    return StyleFactory.createStyle({
      valueSelector: null,
      labelSelector: getPropFromFeature('Value'),
      themeSelector
    });
  }

  static createDefaultIsolineStyle(themeSelector) {
    return StyleFactory.createStyle({
      valueSelector: getLineValueFromFeature,
      labelSelector: getPropFromFeature('Value'),
      themeSelector
    });
  }

  static createDefaultAreaStyle(themeSelector) {
    return StyleFactory.createStyleWithLabel({
      labelSelector: getAreaLabelFromFeature,
      valueSelector: getAreaValueFromFeature,
      themeSelector
    });
  }

  constructor(elements, theme) {
    this.textStyle = null;
    this.fill = null;
    this.stroke = null;
    this.border = null;
    this.elements = elements;
    this.theme = mergeDeep({}, defaultTheme, theme);
  }

  setTheme(newTheme) {
    this.theme = mergeDeep({}, defaultTheme, newTheme);
  }
  /**
   * Returns style by element and view
   *
   * @param {object} params Weather params
   * @param {string} params.element Element
   * @param {string} [params.view] View mode
   * @param {object} [params.options=null] Style options
   * @param {boolean} [params.options.showLabel] Whether to show the textual label.
   */


  getStyle({
    element,
    view,
    options,
    additionalOptions
  }) {
    const elm = this.elements[element];

    if (!elm) {
      // eslint-disable-next-line no-console
      console.warn(`The style does not exist for element "${element}"`);
      return elm;
    }

    const theme = mergeDeep({}, this.theme.COMMON, this.theme[element]);

    if (!theme) {
      // eslint-disable-next-line no-console
      console.warn(`The theme does not exist for element "${element}"`);
    }

    if (view === 'border') {
      return StyleFactory.createBorderStyle(this.theme.border);
    }

    if (typeof elm === 'function') {
      return elm(options, view, theme, additionalOptions);
    }

    const elmView = elm[view];

    if (!elmView) {
      return elm;
    }

    if (Array.isArray(elmView)) {
      return (...args) => elmView.map(e => e(options, view, theme)(...args)).filter(Boolean);
    }

    if (typeof elmView === 'function') {
      return elmView(options, view, theme, additionalOptions);
    }

    return elmView;
  }

}

export default StyleFactory;